import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { NOTE_VALUES, SPIELTYPEN, RATING_STATUS, noteColor, formatDateDE, matchTeamAvg, spieltypLabel, teamWashStats, suggestNextWashPlayer } from "@/lib/matchStats";
import { positionAbbrev, positionColor, sortPlayersByName } from "@/lib/playerConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlayerSelect from "@/components/PlayerSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Trash2, X, Save, MessageSquare, Sparkles, Shirt } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]); // Alle Matches für Wäsche-Statistik
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({}); // {playerId: {note, status, kommentar}}
  const [commentDialogPlayer, setCommentDialogPlayer] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [waescheDraft, setWaescheDraft] = useState(""); // lokale Vorauswahl für Wäsche
  const [savingWaesche, setSavingWaesche] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, allMatches] = await Promise.all([
        api.get(`/matches/${id}`),
        api.get("/players"),
        api.get("/matches"),
      ]);
      setMatch(m.data);
      setRatings(m.data.ratings || {});
      setPlayers(p.data);
      setMatches(allMatches.data);
      setWaescheDraft(m.data.waesche_player_id || "");
      setMetaForm({
        datum: m.data.datum,
        gegner: m.data.gegner,
        spieltyp: m.data.spieltyp,
        heim_auswaerts: m.data.heim_auswaerts || "heim",
        tore_eigen: m.data.tore_eigen ?? "",
        tore_gegner: m.data.tore_gegner ?? "",
      });
    } catch { toast.error("Spiel nicht gefunden"); navigate("/noten"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const persistRatings = async (next) => {
    try {
      await api.put(`/matches/${id}/ratings`, { ratings: next });
    } catch (e) { toast.error("Konnte Bewertung nicht speichern"); }
  };

  const setNote = (playerId, note) => {
    const cur = ratings[playerId] || { status: "starter" };
    if (cur.note === note) {
      // toggle off
      const { [playerId]: _, ...rest } = ratings;
      setRatings(rest);
      persistRatings(rest);
      return;
    }
    const next = { ...ratings, [playerId]: { ...cur, note } };
    setRatings(next);
    persistRatings(next);
  };

  const setStatus = (playerId, status) => {
    const cur = ratings[playerId];
    if (!cur) return;
    const next = { ...ratings, [playerId]: { ...cur, status } };
    setRatings(next);
    persistRatings(next);
  };

  const setKommentar = (playerId, kommentar) => {
    const cur = ratings[playerId];
    if (!cur) return;
    const entry = { ...cur };
    if (kommentar.trim()) entry.kommentar = kommentar.trim();
    else delete entry.kommentar;
    const next = { ...ratings, [playerId]: entry };
    setRatings(next);
    persistRatings(next);
    setCommentDialogPlayer(null);
  };

  const teamAvg = useMemo(() => {
    const notes = Object.values(ratings).map((r) => r.note).filter((n) => typeof n === "number");
    if (notes.length === 0) return null;
    return Math.round((notes.reduce((s,v)=>s+v,0) / notes.length) * 10) / 10;
  }, [ratings]);

  const handleDelete = async () => {
    try { await api.delete(`/matches/${id}`); toast.success("Spiel gelöscht"); navigate("/noten"); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const saveMeta = async () => {
    try {
      const payload = { ...metaForm };
      payload.tore_eigen = payload.tore_eigen === "" ? null : Number(payload.tore_eigen);
      payload.tore_gegner = payload.tore_gegner === "" ? null : Number(payload.tore_gegner);
      const { data } = await api.put(`/matches/${id}`, payload);
      setMatch(data);
      setEditingMeta(false);
      toast.success("Spiel aktualisiert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
  };

  // Spieler alphabetisch (de-Locale) für die Bewertungs-Liste
  const sortedPlayers = useMemo(() => sortPlayersByName(players), [players]);

  // Wäsche-Kandidaten: ausschließlich Spieler, die für dieses Spiel eine Note haben
  const ratedPlayers = useMemo(
    () => sortedPlayers.filter((p) => typeof ratings[p.id]?.note === "number"),
    [sortedPlayers, ratings]
  );

  // Auswahl-Liste: Bewertete Spieler. Falls der aktuell gespeicherte Wäsche-Spieler
  // (warum auch immer) keine Note hat, wird er trotzdem im Dropdown angezeigt,
  // damit der Trainer den Bestand sehen und ändern kann.
  const selectablePlayers = useMemo(() => {
    const saved = match?.waesche_player_id;
    if (!saved) return ratedPlayers;
    if (ratedPlayers.some((p) => p.id === saved)) return ratedPlayers;
    const savedPlayer = players.find((p) => p.id === saved);
    return savedPlayer ? sortPlayersByName([...ratedPlayers, savedPlayer]) : ratedPlayers;
  }, [ratedPlayers, match, players]);

  // Vorschlag berechnen — basiert nur auf bewerteten Spielern, Wäsche-Historie aus allen Matches
  const suggestedWashPlayer = useMemo(() => {
    if (ratedPlayers.length === 0) return null;
    return suggestNextWashPlayer(ratedPlayers, matches);
  }, [ratedPlayers, matches]);

  // Auto-Preselect: nur wenn noch nichts gespeichert UND nichts manuell gewählt
  useEffect(() => {
    if (!match) return;
    if (match.waesche_player_id) return;
    if (waescheDraft) return;
    if (suggestedWashPlayer) setWaescheDraft(suggestedWashPlayer.id);
    // eslint-disable-next-line
  }, [match, suggestedWashPlayer]);

  // Wenn ein bereits gespeicherter Wäsche-Spieler die Note verloren hat → Draft zurücksetzen
  const draftIsValidCandidate = !waescheDraft || selectablePlayers.some((p) => p.id === waescheDraft);

  const suggestWashClick = () => {
    if (!suggestedWashPlayer) {
      toast.error("Kein bewerteter Spieler verfügbar");
      return;
    }
    setWaescheDraft(suggestedWashPlayer.id);
    toast.success(`Vorschlag: ${suggestedWashPlayer.name}`);
  };

  const saveWaesche = async () => {
    setSavingWaesche(true);
    try {
      const { data } = await api.put(`/matches/${id}`, { waesche_player_id: waescheDraft || null });
      setMatch(data);
      toast.success(waescheDraft ? "Wäschedienst gespeichert" : "Wäschedienst entfernt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSavingWaesche(false); }
  };

  if (loading) return <div className="text-center py-20 text-zinc-500">Lädt…</div>;
  if (!match) return null;

  const hasResult = match.tore_eigen != null && match.tore_gegner != null;
  const won = hasResult && match.tore_eigen > match.tore_gegner;
  const lost = hasResult && match.tore_eigen < match.tore_gegner;
  const starterCount = Object.values(ratings).filter((r) => r.status === "starter").length;
  const ersatzCount = Object.values(ratings).filter((r) => r.status === "ersatz").length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <button onClick={() => navigate("/noten")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="md-back-btn">
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1">
            <div className={"inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium tracking-wide mb-2 " + (match.spieltyp === "pflichtspiel" ? "text-brand-lighter bg-brand/10" : "text-amber-300 bg-amber-500/10")}>
              {spieltypLabel(match.spieltyp)} · {match.heim_auswaerts === "auswaerts" ? "Auswärts" : "Heim"}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-100 tracking-tight" data-testid="md-title">
              vs. {match.gegner}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-zinc-400">
              <span>{formatDateDE(match.datum)}</span>
              {hasResult && (
                <span className={"font-display text-lg font-bold " + (won ? "text-emerald-400" : lost ? "text-rose-400" : "text-zinc-300")}>
                  {match.tore_eigen} : {match.tore_gegner}
                </span>
              )}
              {match.waesche_player_id && (() => {
                const w = players.find((p) => p.id === match.waesche_player_id);
                return w ? (
                  <span className="text-xs text-zinc-400 flex items-center gap-1" data-testid="md-waesche">
                    <span className="text-brand-lighter">👕</span> Wäsche: <span className="text-zinc-100 font-medium">{w.name}</span>
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditingMeta(true)}
              className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-10"
              data-testid="md-edit-btn">
              <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
            </Button>
            <Button variant="outline" onClick={() => setConfirmDelete(true)}
              className="bg-transparent border-zinc-700 text-zinc-100 hover:text-red-400 hover:bg-zinc-900 rounded-md h-10"
              data-testid="md-delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-zinc-800">
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Ø Team-Note</div>
            <div className={"font-display text-2xl font-bold tabular-nums " + noteColor(teamAvg)} data-testid="md-team-avg">
              {teamAvg == null ? "—" : teamAvg.toFixed(1)}
            </div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Starter</div>
            <div className="font-display text-2xl font-bold text-emerald-400 tabular-nums">{starterCount}</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Ersatz</div>
            <div className="font-display text-2xl font-bold text-amber-400 tabular-nums">{ersatzCount}</div>
          </div>
        </div>
      </div>

      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Spielerbewertungen</div>
      {sortedPlayers.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
          Keine Spieler im Kader.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ratings-list">
          {sortedPlayers.map((p) => {
            const r = ratings[p.id];
            return (
              <div key={p.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 sm:p-4" data-testid={`rating-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-100 font-medium">{p.name}</span>
                      {r && (
                        <div className="flex gap-1">
                          {RATING_STATUS.map((s) => (
                            <button key={s.key} onClick={() => setStatus(p.id, s.key)}
                              className={
                                "text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold border " +
                                (r.status === s.key
                                  ? (s.key === "starter" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-amber-500/40 bg-amber-500/10 text-amber-400")
                                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300")
                              }
                              data-testid={`rating-status-${p.id}-${s.key}`}>
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={"text-[11px] font-medium tracking-wide mt-0.5 " + positionColor(p.position)}>
                      {positionAbbrev(p.position)} · {p.jahrgang}
                    </div>
                    {r?.kommentar && (
                      <div className="text-xs text-zinc-400 mt-1 italic">„{r.kommentar}"</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Note</div>
                    <div className={"font-display text-3xl font-bold tabular-nums " + noteColor(r?.note)} data-testid={`rating-display-${p.id}`}>
                      {r?.note != null ? r.note.toFixed(1) : "—"}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {NOTE_VALUES.map((n) => {
                    const active = r?.note === n;
                    return (
                      <button key={n} onClick={() => setNote(p.id, n)}
                        className={
                          "min-h-[36px] min-w-[40px] px-2 rounded text-sm font-bold tabular-nums transition border " +
                          (active
                            ? "bg-brand border-brand text-white"
                            : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-brand-lighter/40 hover:text-zinc-100")
                        }
                        data-testid={`rating-note-${p.id}-${n}`}>
                        {n.toFixed(1)}
                      </button>
                    );
                  })}
                  {r && (
                    <button
                      onClick={() => { setCommentDraft(r.kommentar || ""); setCommentDialogPlayer(p); }}
                      className="min-h-[36px] px-3 rounded text-xs font-medium border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center gap-1"
                      data-testid={`rating-comment-${p.id}`}>
                      <MessageSquare className="h-3 w-3" />
                      {r.kommentar ? "Notiz" : "+ Notiz"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Wäschedienst-Karte */}
      <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5" data-testid="md-waesche-card">
        <div className="flex items-center gap-2 mb-3">
          <Shirt className="h-4 w-4 text-brand-lighter" />
          <div className="text-[11px] uppercase tracking-wider text-zinc-400">Wäschedienst</div>
        </div>

        {ratedPlayers.length === 0 ? (
          <div className="text-sm text-zinc-500 italic" data-testid="md-waesche-empty">
            Erst nach Notenvergabe verfügbar. Bewerte oben mindestens einen Spieler.
          </div>
        ) : (
          <>
            <div className="text-xs text-zinc-400 mb-2">
              Nur Spieler mit Spielernote sind auswählbar.
              {suggestedWashPlayer && (
                <span className="ml-1">
                  Fairer Vorschlag:
                  {" "}
                  <span className="text-brand-lighter font-semibold" data-testid="md-waesche-suggested-name">
                    {suggestedWashPlayer.name}
                  </span>
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <PlayerSelect
                  value={draftIsValidCandidate ? waescheDraft : ""}
                  onChange={setWaescheDraft}
                  players={selectablePlayers}
                  placeholder="Spieler wählen"
                  testId="md-waesche-select"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={suggestWashClick}
                className="bg-transparent border-zinc-700 text-brand-lighter hover:bg-zinc-900 rounded-md h-12 sm:w-auto"
                data-testid="md-waesche-suggest-btn"
              >
                <Sparkles className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nächsten Spieler vorschlagen</span>
                <span className="sm:hidden">Vorschlag</span>
              </Button>
              <Button
                type="button"
                onClick={saveWaesche}
                disabled={savingWaesche || (waescheDraft || "") === (match.waesche_player_id || "")}
                className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-12 sm:w-auto"
                data-testid="md-waesche-save-btn"
              >
                <Save className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{savingWaesche ? "Speichern…" : "Übernehmen"}</span>
                <span className="sm:hidden">OK</span>
              </Button>
            </div>

            {match.waesche_player_id && (
              <div className="mt-3 text-xs text-zinc-400" data-testid="md-waesche-current">
                Aktuell gespeichert:
                {" "}
                <span className="text-zinc-100 font-medium">
                  {players.find((p) => p.id === match.waesche_player_id)?.name || "—"}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Comment dialog */}
      <Dialog open={!!commentDialogPlayer} onOpenChange={(o) => !o && setCommentDialogPlayer(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight">
              Notiz · {commentDialogPlayer?.name}
            </DialogTitle>
          </DialogHeader>
          <textarea
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            placeholder="z. B. Tolle Defensivleistung, 1 Assist"
            rows={3}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-md p-3 text-sm text-zinc-100 focus:outline-none focus:border-brand mt-2"
            data-testid="comment-textarea"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCommentDialogPlayer(null)}
              className="flex-1 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800">
              Abbrechen
            </Button>
            <Button onClick={() => setKommentar(commentDialogPlayer.id, commentDraft)}
              className="flex-1 bg-brand hover:bg-brand-light text-white font-semibold"
              data-testid="comment-save-btn">
              <Save className="h-4 w-4 mr-1" /> Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit meta */}
      <Dialog open={editingMeta} onOpenChange={setEditingMeta}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight">Spiel bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs text-zinc-400">Datum</Label>
              <Input type="date" value={metaForm.datum || ""} onChange={(e) => setMetaForm({...metaForm, datum: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 mt-1 rounded-md" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Gegner</Label>
              <Input value={metaForm.gegner || ""} onChange={(e) => setMetaForm({...metaForm, gegner: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 mt-1 rounded-md" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Spieltyp</Label>
              <div className="flex gap-2 mt-1.5">
                {SPIELTYPEN.map((s) => (
                  <button key={s.key} onClick={() => setMetaForm({...metaForm, spieltyp: s.key})}
                    className={
                      "min-h-[40px] px-3 rounded-md border text-sm flex-1 " +
                      (metaForm.spieltyp === s.key ? "bg-brand border-brand text-white" : "bg-zinc-950 border-zinc-800 text-zinc-300")
                    }>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Ergebnis</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min={0} max={99} value={metaForm.tore_eigen ?? ""} onChange={(e) => setMetaForm({...metaForm, tore_eigen: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 rounded-md w-20 text-center" />
                <span className="text-zinc-500">:</span>
                <Input type="number" min={0} max={99} value={metaForm.tore_gegner ?? ""} onChange={(e) => setMetaForm({...metaForm, tore_gegner: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 rounded-md w-20 text-center" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingMeta(false)} className="flex-1 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                <X className="h-4 w-4 mr-1" /> Abbrechen
              </Button>
              <Button onClick={saveMeta} className="flex-1 bg-brand hover:bg-brand-light text-white font-semibold" data-testid="md-meta-save-btn">
                <Save className="h-4 w-4 mr-1" /> Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Spiel löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Alle Bewertungen gehen verloren.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="md-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
